/* eslint-disable react/react-in-jsx-scope */
import { FlatRoutes } from '@backstage/core-app-api';
import {
  convertLegacyAppRoot,
  compatWrapper,
} from '@backstage/core-compat-api';
import { convertLegacyRouteRef } from '@backstage/core-compat-api';
import {
  PageBlueprint,
  createFrontendModule,
  type FrontendFeature,
} from '@backstage/frontend-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import { entityPage } from '../../entityPage';

/*
 * Phase 1 — Register routing shims and legacy plugin APIs.
 *
 * `convertLegacyAppRoot` walks the entity page JSX tree and:
 *   - Creates routing-shim extensions that register every legacy routeRef
 *     (maturity, techdocs, github-actions, snyk, etc.)
 *   - Discovers legacy plugins and converts their APIs to ApiBlueprint
 *   - Creates a catalog FrontendModule with entity content extensions
 *
 * We discard converted FrontendPlugins whose IDs match an NFS alpha
 * plugin we already load in App.tsx (to avoid duplicate registrations).
 * Legacy-only plugins (pagerduty, gitlab, tech-insights, todo, etc.)
 * are kept so their APIs are available.
 */
const allFeatures = convertLegacyAppRoot(
  <FlatRoutes>{[]}</FlatRoutes>,
  { entityPage },
);

/**
 * Plugin IDs that are loaded as NFS alpha plugins (either explicitly in
 * App.tsx or auto-discovered by `createApp` because their package.json
 * exports `./alpha`). Converted duplicates must be discarded to avoid
 * "RouteRef was referenced twice" errors.
 */
const NFS_PLUGIN_IDS = new Set([
  // Explicit alpha imports in App.tsx
  'catalog',
  'api-docs',
  'catalog-import',
  'catalog-graph',
  'kubernetes',
  'org',
  'scaffolder',
  'search',
  'techdocs',
  'notifications',
  'signals',
  'user-settings',
  'app-visualizer',
  // Explicit alpha imports in App.tsx (see githubActionsPlugin, snykPlugin)
  'github-actions', // @backstage-community/plugin-github-actions
  // Legacy createPlugin id is "backstage-plugin-snyk"; NFS alpha uses "snyk" — both must be listed
  // so convertLegacyAppRoot drops the duplicate converted FrontendPlugin.
  'snyk',
  'backstage-plugin-snyk',
  'code-insights', // @roadiehq/backstage-plugin-github-insights
]);

function shouldKeep(feature: FrontendFeature): boolean {
  // Always keep FrontendModules (catalog routing shims, entity extensions)
  if ('$$type' in feature && feature.$$type === '@backstage/FrontendModule') {
    return true;
  }
  // Keep converted FrontendPlugins only for legacy-only community plugins
  if ('$$type' in feature && feature.$$type === '@backstage/FrontendPlugin') {
    const pluginId = 'pluginId' in feature ? (feature as any).pluginId : undefined;
    return pluginId !== undefined && !NFS_PLUGIN_IDS.has(pluginId);
  }
  return true;
}

const entityRoutingAndApis = allFeatures.filter(shouldKeep);

/*
 * Phase 2 — Override the entity page with the legacy EntityLayout tree.
 *
 * The routing shims from Phase 1 register route refs so
 * `createRoutableExtension` components work. `compatWrapper` provides
 * a backwards-compatible AppContext and routing context so that legacy
 * APIs (useApp, useRouteRef) resolve correctly inside the NFS tree.
 *
 * We render the entity page through AsyncEntityProvider (same as the
 * stock CatalogEntityPage) with the full legacy JSX tree as children,
 * preserving exact tab ordering and card layout per entity kind/type.
 */
const entityPageOverride = PageBlueprint.makeWithOverrides({
  name: 'entity',
  factory(originalFactory) {
    return originalFactory({
      path: '/catalog/:namespace/:kind/:name',
      noHeader: true,
      title: 'Catalog Entity',
      routeRef: convertLegacyRouteRef(entityRouteRef),
      loader: async () => {
        const { AsyncEntityProvider } = await import(
          '@backstage/plugin-catalog-react'
        );
        const { useEntityFromUrl } = await import('./useEntityFromUrl');
        const EntityPage = () => {
          const asyncEntity = useEntityFromUrl();
          return compatWrapper(
            <AsyncEntityProvider {...asyncEntity}>
              {entityPage}
            </AsyncEntityProvider>,
          );
        };
        return <EntityPage />;
      },
    });
  },
});

const entityPageOverrideModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [entityPageOverride],
});

export const entityPageFeatures: FrontendFeature[] = [
  ...entityRoutingAndApis,
  entityPageOverrideModule,
];
